/**
 * Shared session row/card state: permissions, action flags, report and join.
 */
import type { SessionGroup } from '@/types/inperson/session.types';
import { getRowPermission } from '@/hooks/usePageActions';
import { useJoinRemoteMeeting } from '@/hooks/useJoinRemoteMeeting';
import { useSessionMinDate } from '@/hooks/useSessionMinDate';
import { useNavigateToReport } from '@/hooks/useNavigateToReport';
import {
  deriveSessionActionFlags,
  deriveSessionGroupDisplay,
  getEstablishmentLabel,
  getInPersonStatusConfig,
  type SessionItemPageActions,
  type SessionListItem,
} from '@/components/sessions/inperson/session-table-utils';

/** Shared row/card props before page-action flags are added. */
interface SessionItemBase {
  session?: SessionListItem;
  group?: SessionGroup;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onEditGroup?: (group: SessionGroup) => void;
  onCancel?: (id: string) => void;
  onCancelGroup?: (id: string, childCount: number) => void;
  onManageAttendance?: (id: string) => void;
}

/** Props for {@link SessionRow} / {@link SessionCard}. */
export interface SessionItemProps extends SessionItemBase {
  pageActions?: SessionItemPageActions;
}

/** Shared row/card state: permissions, tooltips flags, report/join navigation. */
export function useSessionItem({
  session,
  group,
  onManageAttendance,
  pageActions,
}: Pick<SessionItemProps, 'session' | 'group' | 'onManageAttendance' | 'pageActions'>) {
  const {
    navigateToReport,
    navigateToGroupReports,
    isLoading: isLoadingReport,
  } = useNavigateToReport();
  const joinMeeting = useJoinRemoteMeeting();
  const { minSessionDate } = useSessionMinDate(group?.establishmentId);
  const groupDisplay = group ? deriveSessionGroupDisplay(group) : null;
  const sessionFlags = session
    ? deriveSessionActionFlags(session, onManageAttendance, isLoadingReport)
    : null;
  const permissionTarget = session ?? { _permissions: {} };
  const canEdit = getRowPermission(
    permissionTarget,
    'action-edit-row',
    pageActions?.canEditRow ?? true,
  );
  const canCancel = getRowPermission(
    permissionTarget,
    'action-cancel-row',
    pageActions?.canCancelRow ?? true,
  );
  const canViewReport = getRowPermission(
    permissionTarget,
    'action-view-report',
    pageActions?.canViewReport ?? true,
  );
  const canViewReportInformation = getRowPermission(
    permissionTarget,
    'action-view-report-information-meeting',
    pageActions?.canViewReportInformation ?? true,
  );
  const canJoinInformationMeeting = getRowPermission(
    permissionTarget,
    'action-join-meeting-information-meeting',
    pageActions?.canJoinInformationMeeting ?? false,
  );
  const isOutsidePastWindow = group ? group.sessionDate.slice(0, 10) < minSessionDate : false;
  const type = group?.type ?? session?.type ?? '';
  const startTime = group?.startTime ?? session?.startTime ?? '';
  const endTime = group?.endTime ?? session?.endTime ?? '';
  const sessionDate =
    groupDisplay?.sessionDate ?? (session ? new Date(session.sessionDate) : new Date());
  const establishmentLabel = getEstablishmentLabel(group ?? session ?? {});
  const statusConfig =
    groupDisplay?.statusConfig ??
    (session ? getInPersonStatusConfig(session.status, session.type) : null);

  return {
    navigateToReport,
    navigateToGroupReports,
    isLoadingReport,
    joinMeeting,
    groupDisplay,
    sessionFlags,
    canEdit,
    canCancel,
    canViewReport,
    canViewReportInformation,
    canJoinInformationMeeting,
    isOutsidePastWindow,
    type,
    startTime,
    endTime,
    sessionDate,
    establishmentLabel,
    statusConfig,
    itemId: group?.id ?? session?.id,
    editDisabled: groupDisplay
      ? groupDisplay.isTerminated || !groupDisplay.firstActiveId || isOutsidePastWindow
      : (sessionFlags?.editDisabled ?? true),
    attendanceDisabled: groupDisplay
      ? !onManageAttendance || group.status === 'CANCELLED' || !groupDisplay.firstActiveId
      : (sessionFlags?.attendanceDisabled ?? true),
    reportDisabled: groupDisplay
      ? isLoadingReport ||
        (groupDisplay.displayStatus !== 'IN_PROGRESS' &&
          groupDisplay.displayStatus !== 'COMPLETED') ||
        !groupDisplay.firstActiveId
      : (sessionFlags?.reportDisabled ?? true),
    cancelDisabled: groupDisplay
      ? groupDisplay.isTerminated
      : (sessionFlags?.cancelDisabled ?? true),
  };
}
